import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Chip, Dialog, Typography } from "@material-ui/core";
import EventBusyOutlinedIcon from "@material-ui/icons/EventBusyOutlined";
import { useCompany } from "../../Context/CompanyContext";
import { fetchExpiryData } from "../Reports/reportsApi";

const todayKey = () => {
  const d = new Date();
  return `expiryPopup:${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
const wasShownToday = () => {
  try {
    return localStorage.getItem(todayKey()) === "1";
  } catch (error) {
    return false;
  }
};
const markShownToday = () => {
  try {
    localStorage.setItem(todayKey(), "1");
  } catch (error) {
    /* storage blocked - it will simply show again next time */
  }
};

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "";
const dayLabel = (row) =>
  row.daysLeft < 0 ? `${Math.abs(row.daysLeft)}d ago` : row.daysLeft === 0 ? "Today" : `${row.daysLeft}d left`;

// A short, friendly summary shown once a day when the app is opened, if some
// batches are expired or close to expiring. The store can switch it off in
// Settings > Expiry & Batches. "Remind me tomorrow" and "View report" both
// count as seen for today.
const ExpiryAlertDialog = () => {
  const history = useHistory();
  const { company } = useCompany();
  const enabled = company?.batchExpiryEnabled === true && company?.expiryAlertPopup !== false;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ summary: null, rows: [] });

  useEffect(() => {
    if (!enabled || wasShownToday()) return undefined;
    let cancelled = false;
    // Small delay so it lands after the screen has settled, not over a loading spinner.
    const timer = setTimeout(async () => {
      try {
        const { data: res } = await fetchExpiryData({ status: "attention", page: 1, limit: 5 });
        if (cancelled) return;
        const total = (res.summary?.expiredCount || 0) + (res.summary?.nearCount || 0);
        if (total > 0) {
          setData({ summary: res.summary, rows: res.rows || [] });
          setOpen(true);
        }
      } catch (error) {
        /* no pop-up if the check fails */
      }
    }, 2500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled]);

  const dismiss = useCallback(() => {
    markShownToday();
    setOpen(false);
  }, []);

  const goReport = () => {
    dismiss();
    history.push("/reports/expiry");
  };

  const expired = data.summary?.expiredCount || 0;
  const near = data.summary?.nearCount || 0;
  const sentence =
    expired && near
      ? `${expired} batch${expired > 1 ? "es have" : " has"} expired and ${near} ${near > 1 ? "are" : "is"} close to expiring.`
      : expired
      ? `${expired} batch${expired > 1 ? "es have" : " has"} expired.`
      : `${near} batch${near > 1 ? "es are" : " is"} close to expiring.`;

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 20, overflow: "hidden", boxShadow: "0 30px 60px -15px rgba(15,23,42,0.4)" } }}
    >
      <div
        style={{
          padding: "22px 22px 18px",
          color: "#fff",
          background: expired ? "linear-gradient(135deg,#dc2626,#f97316)" : "linear-gradient(135deg,#f59e0b,#fbbf24)",
        }}
      >
        <EventBusyOutlinedIcon style={{ fontSize: 34 }} />
        <Typography style={{ fontWeight: 800, fontSize: "1.25rem", marginTop: 4 }}>Expiry alert</Typography>
        <Typography style={{ opacity: 0.95, fontSize: "0.95rem", marginTop: 2 }}>{sentence}</Typography>
      </div>

      <div style={{ padding: "12px 6px 4px" }}>
        {data.rows.map((row) => {
          const isExpired = row.status === "Expired";
          return (
            <div
              key={`${row.itemId}-${row.batchNo}-${row.expiryDate}`}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: "1px solid #f1f5f9" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {row.itemName}
                </div>
                <div style={{ color: "#64748b", fontSize: "0.78rem" }}>
                  Batch {row.batchNo} · {row.qty} {row.unit} · {fmtDate(row.expiryDate)}
                </div>
              </div>
              <Chip
                size="small"
                label={dayLabel(row)}
                style={{ fontWeight: 800, background: isExpired ? "#fee2e2" : "#fef3c7", color: isExpired ? "#b91c1c" : "#b45309" }}
              />
            </div>
          );
        })}
        {expired + near > data.rows.length && (
          <Typography variant="caption" style={{ display: "block", padding: "8px 16px", color: "#64748b" }}>
            and {expired + near - data.rows.length} more in the report.
          </Typography>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, padding: "12px 16px 16px", justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button onClick={dismiss} style={{ textTransform: "none", fontWeight: 700, color: "#475569" }}>
          Remind me tomorrow
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={goReport}
          style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, background: "#ea580c", color: "#fff" }}
        >
          View expiry report
        </Button>
      </div>
      <Typography variant="caption" style={{ display: "block", padding: "0 16px 12px", color: "#94a3b8", textAlign: "center" }}>
        You can turn this pop-up off in Settings → Expiry &amp; Batches.
      </Typography>
    </Dialog>
  );
};

export default ExpiryAlertDialog;
